import { Box, Text } from '@chakra-ui/react';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { VariantBranchSupplier } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { LoadingOverlayV2 } from '@texas/components/shared/LoadingOverlayV2';
import { TexasDrawerBody } from '@texas/components/shared/drawer/TexasDrawerBody';
import { useRegexSearch } from '@texas/components/shared/hooks/useRegexSearch';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppliersList } from './SuppliersList';
import { LabelValue } from './LabelValue';

export function RecommendedSuppliers({
  variantId,
  branchId,
  branchName,
  branchIdentifier,
  onAdd,
  setIsAddingToBranch,
}: {
  variantId: number;
  branchId: number;
  branchName: string;
  branchIdentifier?: string;
  onAdd?: (data: VariantBranchSupplier) => void;
  setIsAddingToBranch?: (id: number | null) => void;
}) {
  const {
    data: recommendedSuppliers,
    refetch,
    loading,
    error,
    set,
  } = useApiResource(suppliersApi.variant.getRecommendedSuppliers);

  const { t } = useTranslation();
  const { search, setSearch, regexSearch } = useRegexSearch();

  useEffect(() => {
    refetch(variantId, branchId);
  }, [branchId, refetch, variantId]);

  const handleAdded = useCallback(
    (data: VariantBranchSupplier) => {
      set((result) =>
        result
          ? {
              ...result,
              suppliers: result.suppliers.map((supplier) =>
                supplier.id === data.supplierId
                  ? { ...supplier, alreadyAdded: true }
                  : supplier,
              ),
            }
          : null,
      );
      onAdd?.(data);
    },
    [onAdd, set],
  );

  if (!loading && error) {
    return (
      <TexasDrawerBody>
        <Text variant="main">{error.message}</Text>
      </TexasDrawerBody>
    );
  }

  return (
    <>
      <LoadingOverlayV2 loaded={!loading} label={t('general.loading')}>
        <Box
          pos="sticky"
          top={0}
          zIndex={1}
          bg="texas.bg.900"
          _light={{ bg: 'white' }}
          pt={2}
        >
          <TexasDrawerBody>
            <Text variant="main">{t('suppliers.recommendationsBasedOn')}</Text>
            <LabelValue
              label={t('general.productGroup')}
              value={recommendedSuppliers?.productGroup}
            />
            <LabelValue
              label={t('general.state')}
              value={recommendedSuppliers?.state}
            />
          </TexasDrawerBody>

          <SearchFilterInput
            value={search}
            onChange={(searchTerm) => setSearch(searchTerm)}
            debounceDelay={0}
            placeholder={t('suppliers.filterSuppliers')}
          />
        </Box>

        <SuppliersList
          isPdVariant={false}
          variantId={variantId}
          branchId={branchId}
          branchName={branchName}
          branchIdentifier={branchIdentifier}
          setIsAddingToBranch={setIsAddingToBranch}
          suppliers={
            recommendedSuppliers?.suppliers.filter((supplier) =>
              supplier.name.toLowerCase().match(regexSearch),
            ) ?? []
          }
          onAdded={handleAdded}
        />
      </LoadingOverlayV2>
    </>
  );
}
